/**
 * Side elevation of a storage space, by type.
 *
 * Drawn to the space's real proportions — the frame is one user unit per foot,
 * so a 45ft pad is visibly longer than a 20ft one and a covered row's clearance
 * sits where its clearance actually is. A stock photograph of "covered parking"
 * carries none of that, and the whole reason a visitor is reading this card is
 * to work out whether their rig clears the roof.
 */

const FRAME_W = 60;
const FRAME_H = 24;
const GROUND = 21;
const PAD_X = 2;

/** Feet to frame units, so a 50ft space fills the frame and a 20ft one does not. */
const scale = (lengthFt) => Math.min(1, (Number(lengthFt) || 20) / 50);

export default function SpaceTypeIllustration({ spaceType, lengthFt, clearanceFt, className = '' }) {
  const len = (FRAME_W - PAD_X * 2) * scale(lengthFt);
  const x1 = PAD_X;
  const x2 = PAD_X + len;
  // Clearance drawn where it is, falling back to a typical canopy height so a
  // covered row with no recorded clearance still reads as covered.
  const roofY = GROUND - Math.min(FRAME_H - 6, Number(clearanceFt) || 14);

  return (
    <svg viewBox={`0 0 ${FRAME_W} ${FRAME_H}`} className={className}
         preserveAspectRatio="xMidYMax meet" role="presentation" aria-hidden="true">
      {/* The pad, common to every type. */}
      <rect x={x1} y={GROUND - 0.9} width={len} height={1.8} rx={0.4} className="fill-current opacity-25" />
      <line x1={0} y1={GROUND + 1.2} x2={FRAME_W} y2={GROUND + 1.2}
            strokeWidth={0.25} className="stroke-current opacity-20" strokeLinecap="round" />

      {spaceType === 'open' && (
        <>
          {/* Painted stall lines, which is all an uncovered space is. */}
          <line x1={x1} y1={GROUND - 1.2} x2={x1} y2={GROUND - 5} strokeWidth={0.5}
                strokeDasharray="1.4 1.4" className="stroke-current opacity-45" strokeLinecap="round" />
          <line x1={x2} y1={GROUND - 1.2} x2={x2} y2={GROUND - 5} strokeWidth={0.5}
                strokeDasharray="1.4 1.4" className="stroke-current opacity-45" strokeLinecap="round" />
          <circle cx={FRAME_W - 7} cy={5.5} r={2.6} className="fill-current opacity-30" />
        </>
      )}

      {spaceType === 'covered' && (
        <>
          <path d={`M${x1 - 1.5} ${roofY} L${(x1 + x2) / 2} ${roofY - 2.6} L${x2 + 1.5} ${roofY} Z`}
                className="fill-current opacity-80" />
          <rect x={x1 - 1.5} y={roofY} width={len + 3} height={1} className="fill-current opacity-80" />
          <rect x={x1} y={roofY + 1} width={0.9} height={GROUND - roofY - 1} className="fill-current opacity-60" />
          <rect x={x2 - 0.9} y={roofY + 1} width={0.9} height={GROUND - roofY - 1} className="fill-current opacity-60" />
        </>
      )}

      {spaceType === 'enclosed' && (
        <>
          <path d={`M${x1 - 1.5} ${roofY} L${(x1 + x2) / 2} ${roofY - 2.6} L${x2 + 1.5} ${roofY} Z`}
                className="fill-current opacity-80" />
          <rect x={x1 - 0.6} y={roofY} width={len + 1.2} height={GROUND - roofY} rx={0.3}
                className="fill-current opacity-25" />
          <rect x={x1 - 0.6} y={roofY} width={len + 1.2} height={GROUND - roofY} rx={0.3}
                fill="none" strokeWidth={0.7} className="stroke-current opacity-70" />
          {/* Roll-up door: the thing that makes it read as enclosed rather than covered. */}
          <rect x={x1 + len * 0.18} y={roofY + 2} width={len * 0.64} height={GROUND - roofY - 2} rx={0.3}
                className="fill-current opacity-45" />
          {Array.from({ length: 4 }, (_, i) => (
            <line key={i} x1={x1 + len * 0.18} y1={roofY + 4 + i * 2.2}
                  x2={x1 + len * 0.82} y2={roofY + 4 + i * 2.2}
                  strokeWidth={0.3} className="stroke-white opacity-60" />
          ))}
        </>
      )}
    </svg>
  );
}
